import { notification } from 'antd';
import { signOut } from 'aws-amplify/auth';
import i18n from 'i18next';
import { isIOS, osName } from 'react-device-detect';
import {
  NOTIFICATION_TYPE,
  USER_ACTIVITY_EVENT,
  USER_ACTIVITY_TYPE,
  USER_ACTIVITY_USER_TYPE
} from 'src/enums';
import { ApiService } from 'src/services';
import { AppDispatch, documentsActions } from 'src/store'; // Import the AppDispatch type
import { IDocument } from 'src/types/documents.types';
import { IUser } from 'src/types/users.types';
/**
 * @function upperFirstLetter
 * @param {string }text
 * @returns {string} return a input string with first letter as upper case
 */
export const upperFirstLetter = (text: string) => {
  if (!text) {
    return '';
  }
  return text[0].toUpperCase() + text.substring(1);
};

/**
 * @function getErrorMessage
 * @description deep dive into error object to find error message
 * @param {Error} error - error object, response from server
 * @param {string} defaultErrorMessage - optional message to return when err message was not found
 * @returns {string}
 */

export const getErrorMessage = (error: any, defaultErrorMessage?: string) => {
  if (error && typeof error === 'string') return error;
  if (
    error &&
    error.response &&
    error.response.data &&
    error.response.data.message
  ) {
    return error.response.data.message?.message;
  }
  if (error && error.data && error.data.message) return error.data.message;
  if (error && error.message) return error.message;
  if (defaultErrorMessage) return defaultErrorMessage;
  return 'oops something went wrong';
};

export const translateError = (error: string, t?: any) => {
  let errToTranslate = '';
  if (error) {
    if (error === 'Network Error') {
      errToTranslate = 'network_error';
    } else if (error === 'Invalid value in phone_number') {
      errToTranslate = 'invalid_value_in_phone_number';
    } else if (error === 'ERR001') {
      errToTranslate = 'ERR001';
    } else if (error === 'ERR002') {
      errToTranslate = 'ERR002';
    } else if (error === 'ERR003') {
      errToTranslate = 'ERR003';
    } else if (error === 'ERR004') {
      errToTranslate = 'ERR004';
    } else if (error === 'ERR005') {
      errToTranslate = 'ERR005';
    } else if (error === 'ERR006') {
      errToTranslate = 'ERR006';
    } else if (error === 'ERR007') {
      errToTranslate = 'ERR007';
    } else if (error === 'ERR013') {
      errToTranslate = 'ERR013';
    } else if (error === '021') {
      errToTranslate = 'ERR021';
    } else if (error === '003') {
      errToTranslate = 'ERR003';
    } else if (
      error === 'CreateAuthChallenge failed with error Phone not verified.'
    ) {
      errToTranslate = 'ERR001';
    } else {
      errToTranslate = 'oops_something_went_wrong';
    }
  } else {
    errToTranslate = 'oops_something_went_wrong';
  }
  return i18n.t(`erros.${errToTranslate}`);
};

export const makeTextShorter = (text: string, maxlimit: number) => {
  const MAX_LIMIT = maxlimit || 10;
  if (!text) {
    return '';
  }
  if (text.length > MAX_LIMIT) {
    return text.substring(0, MAX_LIMIT - 3) + '...';
  } else {
    return text;
  }
};

export const isPatient = (escortId: string) => {
  return escortId === '1';
};

export const isValidIsraeliID = (id2: string | null | undefined) => {
  if (id2 == '000000802') {
    return true;
  }
  if (id2 == null || id2 == undefined) {
    return false;
  }
  let id: string = String(id2).trim();
  if (id.length > 9 || id.length < 5) return false;

  // Pad string with zeros up to 9 digits
  id = id.length < 9 ? ('00000000' + id).slice(-9) : id;

  return (
    Array.from(id, Number).reduce((counter, digit, i) => {
      const step = digit * ((i % 2) + 1);
      return counter + (step > 9 ? step - 9 : step);
    }) %
      10 ===
    0
  );
};

export const ISRAELI_PHONE_REGEX = /^(05|\+9725|9725)\d-?\d{3}-?\d{4}$/;

export const isRtl = () => {
  return i18n.dir() === 'rtl';
};

// create an activity log
export const createLog = (
  user: IUser | null,
  activityEvent: USER_ACTIVITY_EVENT,
  activityType: USER_ACTIVITY_TYPE,
  activitySubject: string
) => {
  if (!user) return;
  ApiService.createActivityLog({
    esscotId: user.EscortId,
    patientId: user.UserId,
    sessionId: user.SessionId,
    platform: `web-${osName}`,
    userType: USER_ACTIVITY_USER_TYPE.PATIENT,
    activeAdmission: false,
    activityEvent,
    activityType,
    activitySubject: activitySubject || 'none',
    isActive: user.IsActive,
    unitCode: user.MedicalUnit,
    environment: 'patient-web'
  });
};

export const logOut = async () => {
  await signOut();
  window.location.reload();
};

export const onClickDownloadPdf = async (
  item: IDocument,
  dispatch: AppDispatch
): Promise<void> => {
  const { docType, docNum, docVersion, docPart } = item;
  try {
    const res = await ApiService.fetchDocument({
      docType,
      docNum,
      docVersion,
      docPart
    });
    console.log('res.data.fileBinaryRaw', res.fileBinaryRaw);
    // setOpenPdfState({ loader: false, error: null });
    const base64ImageData =
      'data:application/pdf;base64,' + encodeURI(res.fileBinaryRaw);
    const contentType = 'application/pdf';

    const byteCharacters = atob(
      base64ImageData.substr(`data:${contentType};base64,`.length)
    );
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += 1024) {
      const slice = byteCharacters.slice(offset, offset + 1024);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);

      byteArrays.push(byteArray);
    }
    const blob = new Blob(byteArrays, { type: contentType });
    const blobUrl = URL.createObjectURL(blob);
    if (item.isNew || item.isNew === undefined) {
      await dispatch(documentsActions.markDocumentAsRead(item));
    }
    window.open(blobUrl, isIOS ? '_top' : '');
  } catch (error) {
    console.log(error);
  }
};

export const notificationSuccess = (data: {
  message: string;
  description?: string;
}) => {
  const { message, description } = data;
  notification[NOTIFICATION_TYPE.SUCCESS]({
    message,
    description,
    placement: 'bottomRight'
  });
};

export const notificationError = (data: {
  message: string;
  description?: string;
}) => {
  const { message, description } = data;
  notification[NOTIFICATION_TYPE.ERROR]({
    message,
    description,
    placement: 'bottomRight'
  });
};
